// Resolution helpers for the `/category/<slug1>/<slug2>/...` URL scheme.
//
// The backend now publishes `zuper-categories` facet values (lvl0..lvl5) as
// SLUG paths directly (e.g. `lvl1: "trofima > galaktokomika-eidi-psygeiou"`),
// so URL slugs ARE the facet path segments -- building a `/category/...`
// link, or an InstantSearch hierarchicalMenu refinement, from a slug path is
// pure string manipulation, no taxonomy lookup required (see
// `slugPathToURL` below). The taxonomy -- fetched at runtime via
// `getTaxonomy()`, published by zuper-data -- is needed only to (a)
// validate that a slug path actually exists (404 otherwise) and (b)
// resolve slugs to human display titles for pages/widgets that don't
// already have those titles locally (records carry their own `titles`
// array now -- see category-breadcrumb.tsx -- so this is mostly needed by
// the /category/* route and the search-page widgets, which only see raw
// facet slugs).

import { useEffect, useState } from 'react';

export const CATEGORY_ATTRIBUTES = [
  'zuper-categories.lvl0',
  'zuper-categories.lvl1',
  'zuper-categories.lvl2',
  'zuper-categories.lvl3',
  'zuper-categories.lvl4',
  'zuper-categories.lvl5',
];
export const CATEGORY_SEPARATOR = ' > ';

// Parcel inlines `process.env.TAXONOMY_URL` at build time when it's
// referenced as a full literal expression like this (same mechanism as
// `ALGOLIA_INDEX` in algolia.ts). Defaults to this app's own bucket --
// zuper-data publishes the taxonomy alongside the built site, so this is a
// same-origin request in production (no CORS concerns there). Overridden
// for local dev, where the Parcel dev server doesn't serve `/data/*` --
// see README for the local static-server workflow.
export const TAXONOMY_URL = process.env.TAXONOMY_URL ?? '/data/categories.json';

type TaxonomyNode = {
  t: string; // title
  s: string; // URL slug
  c?: TaxonomyNode[]; // children (absent on leaves)
};

let taxonomyPromise: Promise<TaxonomyNode[]> | null = null;

// A single fetch+parse attempt, no retry/logging -- used both for the
// initial attempt and the one retry below.
function fetchTaxonomyOnce(): Promise<TaxonomyNode[]> {
  return fetch(TAXONOMY_URL).then((res) => {
    if (!res.ok) {
      throw new Error(`Failed to fetch taxonomy: HTTP ${res.status}`);
    }
    return res.json();
  });
}

// A dev server started before .proxyrc.json existed (or before a restart
// picked it up) serves the SPA fallback for `/data/categories.json`
// instead of proxying it -- `res.ok` is true but the body is HTML, not
// JSON, so `res.json()` throws here just the same as a network/HTTP
// failure. A momentary network blip looks the same too. Either way, one
// retry a few seconds later self-heals without ever bothering the user, so
// this is worth exactly one retry (not an open-ended loop) before settling
// into "labels are slugs for this session."
const TAXONOMY_RETRY_DELAY_MS = 5000;

// Fetches the published category taxonomy once per session -- this
// module-level promise is the single cache shared by every caller
// (getTaxonomy() itself, slugPathToTitles(), useTaxonomy(), and the
// /category/* route loader), so a transient failure is logged and retried
// exactly once for the whole page, not once per consumer. Still clears the
// cache on a final (post-retry) failure so a later caller -- e.g. a fresh
// page navigation -- gets a fresh attempt rather than a permanently
// rejected promise.
export function getTaxonomy(): Promise<TaxonomyNode[]> {
  if (!taxonomyPromise) {
    taxonomyPromise = fetchTaxonomyOnce().catch((e) => {
      console.error(
        'taxonomy fetch failed -- category labels will show slugs; dev ' +
          'servers need .proxyrc.json + restart',
        e
      );
      return new Promise<TaxonomyNode[]>((resolve, reject) => {
        setTimeout(() => {
          fetchTaxonomyOnce().then(resolve, (e2) => {
            taxonomyPromise = null;
            reject(e2);
          });
        }, TAXONOMY_RETRY_DELAY_MS);
      });
    });
  }
  return taxonomyPromise;
}

// Walks `tree` along `slugs`, matching each segment's `s` field exactly at
// its level; returns null as soon as one level doesn't match (or if
// `slugs` is empty).
function resolveSlugTitles(
  tree: TaxonomyNode[],
  slugs: string[]
): string[] | null {
  if (!slugs || slugs.length === 0) return null;

  let nodes = tree;
  const titles: string[] = [];

  for (const slug of slugs) {
    const match = nodes.find((node) => node.s === slug);
    if (!match) return null;
    titles.push(match.t);
    nodes = match.c || [];
  }

  return titles;
}

// Slug path (e.g. the `/category/*` URL splat, already split) -> display
// titles, fetching+validating against the taxonomy. Resolves to null when
// any segment doesn't match -- callers (the /category/* route loader)
// should treat that as a 404.
export async function slugPathToTitles(
  slugs: string[]
): Promise<string[] | null> {
  const tree = await getTaxonomy();
  return resolveSlugTitles(tree, slugs);
}

// Slug path -> the pretty `/category/<s1>/<s2>/...` URL. Pure string ops --
// no taxonomy needed, since URL slugs are exactly the facet path segments
// (facet item values, record lvl values) now.
export function slugPathToURL(slugs: string[]): string | null {
  if (!slugs || slugs.length === 0) return null;
  return `/category/${slugs.map(encodeURIComponent).join('/')}`;
}

// The deepest (last) title in a resolved path -- e.g. for an <h1>/<title>.
export function categoryTitle(titles: string[]): string {
  return titles && titles.length > 0 ? titles[titles.length - 1] : '';
}

// The full path as human-readable text, e.g. for a meta description --
// "Τρόφιμα › Γαλακτοκομικά & Είδη Ψυγείου".
export function categoryPathLabel(titles: string[]): string {
  return (titles || []).join(' › ');
}

// React hook for widgets that only see raw facet slugs (CategoryNav's
// chips/breadcrumb, the left-panel top-level list in page-search.tsx) and
// need a display title for them. Returns a resolver function: given a slug
// path, it returns the deepest title once the taxonomy has loaded, or the
// raw last slug while loading / on fetch failure / if the path doesn't
// resolve -- so labels are never blank, just briefly (or permanently, on
// failure) un-prettified. The logging + one-retry behaviour on failure
// lives in getTaxonomy() above, shared by every consumer -- this hook
// itself just swallows a final (post-retry) rejection, same as before.
export function useTaxonomy(): (slugs: string[]) => string {
  const [tree, setTree] = useState<TaxonomyNode[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTaxonomy()
      .then((t) => {
        if (!cancelled) setTree(t);
      })
      .catch(() => {
        // Swallowed -- the resolver below falls back to the raw slug.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (slugs: string[]) => {
    const fallback = slugs && slugs.length > 0 ? slugs[slugs.length - 1] : '';
    if (!tree) return fallback;
    const titles = resolveSlugTitles(tree, slugs);
    return titles ? titles[titles.length - 1] : fallback;
  };
}

// Appends the current free-text search query to a link target so it
// survives category navigation (e.g. clicking a chip while `?q=milk` is
// active keeps you searching "milk" within the new category).
export function withQuery(url: string, query?: string): string {
  return query ? `${url}?q=${encodeURIComponent(query)}` : url;
}
